import React, { useState, createContext, useContext, useEffect, useRef } from "react";
import { FormControl, FormErrorMessage, VStack, HStack, Icon, useColorMode, Input, Box } from "@chakra-ui/react";
import { Field, Form, Formik } from "formik";
import { ArrowRight } from "phosphor-react";
import { setJWT, fetchWithN8n, Authorized, Anonymous, useUserData } from "@com.marathonium/web2-utils";

import { TextAccent } from "../Text";
import { ButtonPrimary } from "../Button";

import {
  COLOR_PINK,
  FONT_SIZE_16,
  FONT_SIZE_24,
  SPACING_10,
  SPACING_30,
  COLOR_WHITE,
  COLOR_BLACK_OPACITY_05,
  COLOR_BLACK,
  SPACING_20,
  BORDER_RADIUS_20,
  SPACING_65,
} from "../../constants/styles";
import { ConfirmCodeError, ConfirmCodeFormTitle, ConfirmCodeInput, ConfirmFormButtons } from "../LoginWidget/LoginWidget";

function changeRequest(email) {
  return fetchWithN8n("web.user.change_email_request.v1", { email });
}

function confirmRequest({ id, code }) {
  return fetchWithN8n("web.user.change_email_confirm.v1", { id, code });
}

const EmailWidgetContext = createContext();

const EmailWidgetContextProvider = ({ children, onSendConfirm, colorMode }) => {
  const [confirmState, setConfirmState] = useState(null);
  const { colorMode: themeColorMode } = useColorMode();

  function changeEmail(email) {
    return changeRequest(email).then(resp => {
      setConfirmState({ ...resp, email });
      onSendConfirm();
    });
  }

  function resendCode() {
    return changeRequest(confirmState.email).then(resp => {
      setConfirmState({ ...resp, email: confirmState.email });
    });
  }

  function confirmCode(code) {
    return confirmRequest({ id: confirmState.id, code }).then(resp => {
      if (!resp.token) {
        throw new Error("Произошла ошибка. Попробуйте снова");
      }

      return resp;
    });
  }

  function cancelConfirm() {
    setConfirmState(null);
  }

  return (
    <EmailWidgetContext.Provider
      value={{
        confirmState,

        changeEmail,
        resendCode,
        confirmCode,
        cancelConfirm,

        colorMode: colorMode || themeColorMode,
      }}
    >
      {children}
    </EmailWidgetContext.Provider>
  );
};

function ConfirmCodeStep({ onSuccess }) {
  const { confirmState, resendCode, confirmCode, cancelConfirm, colorMode } = useContext(EmailWidgetContext);
  const { expired_at, page_settings, email } = confirmState;

  const [isDisabled, setIsDisabled] = useState(false);
  const [error, setError] = useState(null);

  function onSubmit(code) {
    setIsDisabled(true);

    confirmCode(code)
      .then(r => {
        setJWT(r.token);
        return r;
      })
      .then(onSuccess)
      .catch(e => {
        setError(e.message);
      })
      .finally(() => {
        setIsDisabled(false);
      });
  }

  function onRetry() {
    return resendCode().catch(() => {
      setError(`Не удалось отправить проверочный код. Попробуйте ещё раз`);
    });
  }

  const [pinTimestamp, setPinTimestamp] = useState(new Date().getTime());
  function clearPinInput() {
    setPinTimestamp(new Date().getTime());
    setError(null);
  }

  return (
    <VStack alignSelf="center" spacing={SPACING_20} w="100%" maxW="400px">
      <ConfirmCodeFormTitle title={`На ${email} был отправлен код подверждения, пожалуйста введите его в поле ниже:`} />

      <VStack spacing={SPACING_10}>
        <ConfirmCodeInput
          renderID={pinTimestamp}
          isDisabled={isDisabled}
          onComplete={onSubmit}
          onChange={() => setError(null)}
          colorMode={colorMode}
        />

        <ConfirmCodeError error={error} onClick={clearPinInput} />
      </VStack>

      <ConfirmFormButtons
        pageSettings={page_settings}
        expiredAt={expired_at}
        onRetry={onRetry}
        isDisabled={isDisabled}
        onCancel={cancelConfirm}
      />
    </VStack>
  );
}

function ChangeEmailForm() {
  const { changeEmail, colorMode } = useContext(EmailWidgetContext);
  const userData = useUserData();
  const initialEmail = (userData || {}).email || "";

  const [error, setError] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    if (initialEmail) {
      setLocked(true);
    }
  }, [initialEmail]);

  const inputRef = useRef();
  const setFocus = () => {
    setTimeout(() => {
      inputRef.current.focus();
    }, 100);
  };

  function onSubmit({ email }) {
    if (email === initialEmail) {
      setLocked(true);
      return;
    }

    setDisabled(true);

    changeEmail(email)
      .catch(e => {
        setError(e.message);
      })
      .finally(() => {
        setDisabled(false);
      });
  }

  return (
    <VStack spacing={SPACING_10} align="stretch" w="100%">
      <Formik
        initialValues={{
          email: initialEmail,
        }}
        enableReinitialize
        onSubmit={onSubmit}
      >
        <Form>
          <VStack align="stretch" spacing={SPACING_30} w="100%">
            <Field name="email">
              {({ field }) => (
                <FormControl isRequired isInvalid={error} isDisabled={locked || disabled}>
                  <VStack w="100%">
                    <Input
                      ref={inputRef}
                      {...field}
                      type="email"
                      color={colorMode === "light" ? COLOR_BLACK : COLOR_WHITE}
                      background={colorMode === "light" ? COLOR_WHITE : COLOR_BLACK_OPACITY_05}
                      border={colorMode === "light" ? null : "none"}
                      borderRadius={BORDER_RADIUS_20}
                      height={SPACING_65}
                    />

                    <FormErrorMessage color={COLOR_PINK} fontSize={FONT_SIZE_16}>
                      {error}
                    </FormErrorMessage>
                  </VStack>
                </FormControl>
              )}
            </Field>

            {locked ? (
              <ButtonPrimary
                type="button"
                onClick={e => {
                  e.preventDefault();
                  setLocked(false);
                  setFocus();
                }}
              >
                <TextAccent>Изменить почту</TextAccent>
              </ButtonPrimary>
            ) : (
              <ButtonPrimary type="submit" disabled={disabled}>
                <HStack>
                  <TextAccent>Подтвердить</TextAccent>
                  <Icon as={ArrowRight} fontSize={FONT_SIZE_24} />
                </HStack>
              </ButtonPrimary>
            )}
          </VStack>
        </Form>
      </Formik>
    </VStack>
  );
}

function ChangeEmailStep({ header, footer }) {
  return (
    <VStack alignSelf="center" spacing={SPACING_20} w="100%" maxW="400px">
      <React.Fragment>{header}</React.Fragment>
      <ChangeEmailForm />
      <React.Fragment>{footer}</React.Fragment>
    </VStack>
  );
}

function EmailWidgetView({ onSuccess, header, footer }) {
  const { confirmState } = useContext(EmailWidgetContext);

  if (confirmState) {
    return <ConfirmCodeStep onSuccess={onSuccess} />;
  }

  return <ChangeEmailStep header={header} footer={footer} />;
}

export function EmailWidget({ onSuccess, onSendConfirm, header, footer, colorMode, anonymousContent, ...props }) {
  return (
    <React.Fragment>
      <Anonymous>{anonymousContent}</Anonymous>
      <Authorized>
        <Box {...props}>
          <EmailWidgetContextProvider onSendConfirm={onSendConfirm} colorMode={colorMode}>
            <EmailWidgetView onSuccess={onSuccess} header={header} footer={footer} />
          </EmailWidgetContextProvider>
        </Box>
      </Authorized>
    </React.Fragment>
  );
}
